// make sure we have a working `fetch` implementation in browser
import 'whatwg-fetch'

import PropTypes from 'prop-types'
import React, { useState, useRef, useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSchNetApiClient } from './SchNetApi'
import useScrolledToElement from '../hooks/useScrolledToElement'

import 'navigator.sendbeacon'

import {
  withTracker,
  withQueryClient,
  withSchNetApiClient,
  withCrmApiClient,
  withOrderApiClient,
} from './utils'

import FilterForm from './components/FilterForm'
import ProgramList from './components/ProgramList'
import { FLAG_DEFAULTS } from './constants'

import ErrorPage from './components/ErrorPage'
import packageJson from '../../package.json'

import { default as CatalogOrderComponent } from '../CatalogOrder'

const { name: APP_NAME, version: APP_VERSION } = packageJson
/*
   Component to render filterable list of schueleraustausch.net programs.
   Contains catalog order feature.

   NOTE: this component expects to be wrapped in multiple providers which
   are added on export and the result is exported as the default.
   If you want to customize which providers are added
   (e.g. if you already are within a queryclient or tracking provider)

   ```js
   import { BareSchNetProgramsComponent, withOrderApiClient, withSchNetApiClient, withCrmApiClient } from 'sch_net_programs_component'
   ```

   and then only wrap the Component in the providers you need:

   ```
   const MySchNetProgramsComponent = withOrderApiClient(withSchNetApiClient(withCrmApiClient(BareSchNetProgramsComponent)))
   ```

   and use `MySchNetProgramsComponent` in JSX.
*/
const BareSchNetProgramsComponent = (props) => {
  useEffect(() => {
    navigator.sendBeacon(
      `https://api.apps.austauschkompass.de/${APP_NAME}/track`,
      JSON.stringify({ version: APP_VERSION, url: document.location.href })
    )
  }, [])

  // userFilter enabled by default
  const userFilter = (props.userFilter === false) ? false : true
  // program count info depends on userFilter display
  const showInfo = userFilter

  const schNetApiClient = useSchNetApiClient()
  const [organisationId, setOrganisationId] = useState()
  const [interestInSchoolPrograms, setInterestInSchoolPrograms] = useState(false)
  const [programId, setProgramId] = useState()
  const [sortOrder, setSortOrder] = useState('popularity')
  const [filterOpen, setFilterOpen] = useState(false)
  const [filter, setFilter] = useState({
    countryCode: props.filter.countryCode || 'US',
    duration: props.filter.duration || '',
    startYear: props.filter.startYear,
    price: props.filter.price,
    flags: FLAG_DEFAULTS,
    organisationId: props.filter.organisationId,
  })
  const [scrollEnabled, setScrollEnabled] = useState(false)
  const scrolledToElement = useScrolledToElement({ enabled: scrollEnabled })

  const handleOrderFinish = () => {
    setOrganisationId(null)
    setProgramId(null)
    setInterestInSchoolPrograms(false)
  }

  const handleOrderClickForOrga = ({ organisationId, programType, programId }) => {
    setScrollEnabled(true)
    setOrganisationId(organisationId)
    setProgramId(programId)
    setInterestInSchoolPrograms(programType === 'school_choice_program')
  }

  const handleFilterChange = (param, value) => {
    // Copy nested filter object to avoid modifying a state-variable
    //
    // see: https://react.dev/learn/updating-objects-in-state#updating-a-nested-object
    const newFilter = {
      ...filter,
      flags: {
        ...filter.flags
      }
    }

    newFilter[param] = value
    setFilter(newFilter)
  }

  const handleFlagChange = (flags) => {
    handleFilterChange('flags', flags)
  }

  const handleFilterToggle = () => {
    setFilterOpen(!filterOpen)
  }

  const {
    data: countries,
    isError: countriesIsError,
    isLoading: countriesIsLoading,
    error: countriesError,
  } = useQuery(['countries'], schNetApiClient.fetchCountries)

  if (countriesIsLoading) {
    return (
      <div ref={scrolledToElement}>
        <div className="d-flex flex-column align-items-center">
          <p>Loading...</p>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  if (countriesIsError) {
    return (
      <div ref={scrolledToElement}>
        <ErrorPage
          message={countriesError && countriesError.message}
        />
      </div>
    )
  }

  if (organisationId) {
    return (
      <div className='row'>
        <div className='ak-catalog-order-wrapper'>
          <CatalogOrderComponent
            orderApiUrl={props.orderApiUrl}
            orderApiToken={props.orderApiToken}
            crmApiUrl={props.crmApiUrl}
            crmApiToken={props.crmApiToken}
            businessArea='schueleraustausch'
            referrer={props.referrer}
            organisationId={organisationId}
            interestInSchoolPrograms={interestInSchoolPrograms}
            programId={programId.toString()}
            targetCountryCode={filter.countryCode}
            onFinish={handleOrderFinish}
            upsell={props.upsell}
            upsellPrechecked={props.upsellPrechecked}
            exchangeCountryCode={filter.countryCode}
          />
        </div>
      </div>
    )
  } else {
    return (
      <div ref={scrolledToElement}>
        {userFilter ? (
          <FilterForm
            open={filterOpen}
            params={filter}
            sortOrder={sortOrder}
            countries={countries}
            onChange={handleFilterChange}
            onFlagChange={handleFlagChange}
            onSortOrderChange={setSortOrder}
            onToggle={handleFilterToggle}
            containerRef={scrolledToElement}
            startYearRangeStart={props.startYearRangeStart}
          />
        ) : null}
        <ProgramList
          onOrderClickedForOrga={handleOrderClickForOrga}
          sortOrder={sortOrder}
          narrow={props.narrow}
          limit={props.limit}
          showInfo={showInfo}
          filter={filter}
          ratingsLinkPrefix={props.ratingsLinkPrefix}
        />
      </div>
    )
  }
}

BareSchNetProgramsComponent.propTypes = {
  organisationId: PropTypes.string,
  referrer: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  duration: PropTypes.number,
  startYear: PropTypes.number,
  limit: PropTypes.number,
  userFilter: PropTypes.bool,
  ratingsLinkPrefix: PropTypes.string,
  startYearRangeStart: PropTypes.number,
  upsell: PropTypes.bool.isRequired,
  upsellPrechecked: PropTypes.bool.isRequired,
  narrow: PropTypes.bool,
}

const SchNetProgramsComponent =
  withTracker(
    withQueryClient(
      withSchNetApiClient(
        withCrmApiClient(
          withOrderApiClient(
            BareSchNetProgramsComponent
          )
        )
      )
    )
  )

SchNetProgramsComponent.propTypes = {
  organisationId: PropTypes.string,
  referrer: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  duration: PropTypes.number,
  startYear: PropTypes.number,
  limit: PropTypes.number,
  userFilter: PropTypes.bool,
  programApiUrl: PropTypes.string.isRequired,
  orderApiUrl: PropTypes.string.isRequired,
  orderApiToken: PropTypes.string.isRequired,
  crmApiUrl: PropTypes.string,
  crmApiToken: PropTypes.string,
  gaId: PropTypes.string,
  mtId: PropTypes.string,
  mtUrl: PropTypes.string,
  ratingsLinkPrefix: PropTypes.string,
  startYearRangeStart: PropTypes.number,
  upsell: PropTypes.bool.isRequired,
  upsellPrechecked: PropTypes.bool.isRequired,
  narrow: PropTypes.bool,
  queryClientOptions: PropTypes.object,
}

export {
  SchNetProgramsComponent as default,
  BareSchNetProgramsComponent,
  withSchNetApiClient,
  withCrmApiClient,
  withOrderApiClient,
  withQueryClient,
  withTracker,
}
